import { ResponsiveSwarmPlot, ComputedDatum } from '@nivo/swarmplot';
import { useState, forwardRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { EnrichmentPoint, EnrichmentSwarmPlotProps } from '../types';
import { getColorByLogP, parseRatioToNumber } from '../helpers';
import ColorLegend from './Legends/ColorLegend';
import SizeLegend from './Legends/SizeLegend';
import DataTable from './DataTable';
import { userAppContext } from '@/context/Context';
import { UserContext } from '@/types';
import SharedButton from '@/global/Button/Button';
import { trackEvent } from '@/utils/analytics';

type PlotMetric = 'bgRatio' | 'intersection' | 'oddsRatio' | 'geneRatio';

const SwarmPlotChart = ({
  data,
  groupKeys,
  minPlotValue,
  maxPlotValue,
  plotMetric,
  onHover,
  isDownload = false,
}: {
  data: EnrichmentPoint[];
  groupKeys: string[];
  minPlotValue: number;
  maxPlotValue: number;
  plotMetric: PlotMetric;
  onHover?: (e: any, fullName: string) => void;
  isDownload?: boolean;
}) => {
  const safeMinPlot = minPlotValue > 0 ? minPlotValue * 0.9 : 0;
  const safeMaxPlot = maxPlotValue > 0 ? maxPlotValue * 1.1 : 1;
  const valueFormat = plotMetric === 'bgRatio' ? '.6f' : '.0f';
  const valueLegend: string = (() => {
    switch (plotMetric) {
      case 'bgRatio':
        return 'bgRatio (numeric)';
      case 'oddsRatio':
        return 'Odds Ratio (numeric)';
      case 'intersection':
        return 'Intersection (numeric)';
      case 'geneRatio':
        return 'Gene Ratio (numeric)';
      default:
        return 'bgRatio (numeric)';
    }
  })();

  const commonProps = {
    data,
    groups: groupKeys.length ? groupKeys : ['ALL'],
    value: 'value' as const,
    valueFormat: valueFormat as any,
    valueScale: { type: 'linear' as const, min: safeMinPlot, max: safeMaxPlot },
    size: (node: any) => node?.size || node?.data?.size || 15,
    colors: (node: any) => node?.color || node?.data?.color || '#999999',
    forceStrength: 3,
    simulationIterations: 150,
    borderColor: { from: 'color', modifiers: [['darker', 0.6]] } as any,
    margin: { top: 80, right: 100, bottom: 80, left: 350 },
    layout: 'horizontal' as const,
    axisTop: null,
    axisRight: null,
    axisBottom: {
      tickSize: 10,
      tickPadding: 5,
      tickRotation: -45,
      tickValues: 5,
      legend: valueLegend,
      legendPosition: 'middle' as const,
      legendOffset: 50,
    },
    axisLeft: {
      tickSize: 5,
      tickPadding: 15,
      tickRotation: 0,
      legend:
        plotMetric === 'bgRatio'
          ? 'Disease (top 20 by bgRatio)'
          : plotMetric === 'oddsRatio'
            ? 'Disease (top 20 by Odds Ratio)'
            : 'Disease (top 20 by Intersection)',
      legendPosition: 'middle' as const,
      legendOffset: -300,
      ...(isDownload
        ? {}
        : {
            renderTick: (tick: any) => {
              const fullName = data.find(d => d.group === tick.value)?.diseaseName || tick.value;
              return (
                <g
                  transform={`translate(${tick.x},${tick.y})`}
                  onMouseEnter={e => onHover?.(e, fullName)}
                  onMouseLeave={() => onHover?.(null, '')}
                  style={{ cursor: 'default' }}
                >
                  <text
                    textAnchor="end"
                    dominantBaseline="central"
                    style={{ fontSize: 11, fill: '#333', pointerEvents: 'all' }}
                  >
                    {tick.value}
                  </text>
                </g>
              );
            },
          }),
    },
    animate: !isDownload,
    ...(isDownload
      ? {}
      : {
          motionConfig: 'gentle' as const,
          tooltip: (node: ComputedDatum<EnrichmentPoint>) => (
            <div className="bg-white px-3 py-2 shadow-lg rounded border border-gray-200">
              <div className="font-semibold text-sm">{node.data.diseaseName}</div>
              <div className="text-xs text-gray-600 mt-1">
                <div>p-value: {Number(node.data.pvalue).toExponential(2)}</div>
                <div>-log10(p): {(-Math.log10(node.data.pvalue)).toFixed(2)}</div>
                <div>bgRatio: {Number(node.data.bgRatioNum).toFixed(6)}</div>
                <div>oddsRatio: {Number(node.data.oddsRatioNum).toFixed(6)}</div>
                <div>Intersection: {node.data.intersectionSize}</div>
              </div>
            </div>
          ),
        }),
  };

  return <ResponsiveSwarmPlot<EnrichmentPoint> {...commonProps} />;
};

const MobileBarChart = ({ data, plotMetric }: { data: EnrichmentPoint[]; plotMetric: PlotMetric }) => {
  const [expanded, setExpanded] = useState(false);
  const displayData = expanded ? data : data.slice(0, 8);
  const maxLogP = 12;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-sm">
          Top Diseases by{' '}
          {plotMetric === 'bgRatio'
            ? 'bgRatio'
            : plotMetric === 'oddsRatio'
              ? 'Odds Ratio'
              : 'Intersection size'}
        </h3>
        {data.length > 8 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 flex items-center gap-1 font-medium"
          >
            {expanded ? (
              <>
                Show less <ChevronUpIcon className="h-4 w-4" />
              </>
            ) : (
              <>
                Show all ({data.length}) <ChevronDownIcon className="h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>

      <div className="space-y-4">
        {displayData.map((item, idx) => {
          const logP = item.pvalue > 0 ? -Math.log10(item.pvalue) : 100;
          const barWidth = Math.min((logP / maxLogP) * 100, 100);

          return (
            <div key={idx} className="space-y-1.5">
              <div className="flex justify-between items-start">
                <span className="font-medium text-gray-800 text-sm flex-1 pr-2 leading-tight">
                  {idx + 1}. {item.diseaseName}
                </span>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded text-white flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                >
                  {logP.toFixed(1)}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>

              <div className="flex justify-between text-xs text-gray-600">
                <span>
                  <span className="font-medium">p-value:</span> {Number(item.pvalue).toExponential(1)}
                </span>
                <span>
                  <span className="font-medium">Intersection:</span> {item.intersectionSize}
                </span>
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">bgRatio:</span> {Number(item.bgRatioNum).toFixed(4)}
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Odds Ratio:</span> {Number(item.oddsRatioNum).toFixed(6)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-3 bg-gray-50 rounded-lg">
        <div className="font-semibold mb-2 text-xs text-gray-700">Color Scale (-log10 p-value):</div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: '#fc0314' }}></div>
            <span className="text-gray-700">High significance (&gt; 8)</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: '#c5008f' }}></div>
            <span className="text-gray-700">Medium (5-8)</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: '#1e01fd' }}></div>
            <span className="text-gray-700">Low (&lt; 5)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MAX_DISEASES_IN_GRAPH = 50;

const EnrichmentSwarmPlot = forwardRef<HTMLDivElement, EnrichmentSwarmPlotProps>(
  ({ data, onDownload, geneFilterType }, ref) => {
    const [plotMetric, setPlotMetric] = useState<PlotMetric>('bgRatio');
    const [sizeMetric, setSizeMetric] = useState<'bgRatio' | 'oddsRatio' | 'intersection'>('intersection');
    const [hoveredLabel, setHoveredLabel] = useState<{ name: string; x: number; y: number } | null>(null);
    const [selectedDiseaseIds, setSelectedDiseaseIds] = useState<Set<string>>(new Set());
    const navigate = useNavigate();
    const context = userAppContext() as unknown as UserContext & { restrictSources?: string | false };
    const { user } = context;
    const isTrial = user?.['cognito:groups']?.[0] === 'ROLE_TRIAL';

    const handleSelectionChange = useCallback((ids: string[]) => {
      setSelectedDiseaseIds(new Set(ids));
    }, []);

    const processedData = useMemo(() => {
      if (!data || data.length === 0) return null;

      const sortedBySignificance = [...data].sort((a, b) => (a.pvalue ?? 1) - (b.pvalue ?? 1));
      // const limitedRawData = sortedBySignificance.slice(0, 20);
      const allRawData = sortedBySignificance;

      const intersectionSizes = allRawData.map(d => d.intersectionSize ?? 0).filter(s => s > 0);
      const minIntersection = intersectionSizes.length > 0 ? Math.min(...intersectionSizes) : 1;
      const maxIntersection = intersectionSizes.length > 0 ? Math.max(...intersectionSizes) : 1;

      const bgRatioValues = allRawData.map(d => parseRatioToNumber(d.bgRatio)).filter(r => r > 0);
      const minBgRatioForSize = bgRatioValues.length > 0 ? Math.min(...bgRatioValues) : 0;
      const maxBgRatioForSize = bgRatioValues.length > 0 ? Math.max(...bgRatioValues) : 1;

      const oddsRatioValuesForSize = allRawData
        .map(d => (typeof d.oddsRatio === 'number' ? d.oddsRatio : 0))
        .filter(r => r > 0);
      const minOddsRatioForSize = oddsRatioValuesForSize.length > 0 ? Math.min(...oddsRatioValuesForSize) : 0;
      const maxOddsRatioForSize = oddsRatioValuesForSize.length > 0 ? Math.max(...oddsRatioValuesForSize) : 1;

      const calculateSize = (
        intersection: number,
        bgRatio: number,
        oddsRatio: number,
        metric: 'bgRatio' | 'oddsRatio' | 'intersection',
      ): number => {
        const minSize = 7;
        const maxSize = 28;

        let value: number;
        let minValue: number;
        let maxValue: number;

        switch (metric) {
          case 'bgRatio':
            value = bgRatio;
            minValue = minBgRatioForSize;
            maxValue = maxBgRatioForSize;
            break;
          case 'oddsRatio':
            value = oddsRatio;
            minValue = minOddsRatioForSize;
            maxValue = maxOddsRatioForSize;
            break;
          case 'intersection':
          default:
            value = intersection;
            minValue = minIntersection;
            maxValue = maxIntersection;
            break;
        }

        if (!value || value <= 0) return minSize;
        if (minValue === maxValue) return (minSize + maxSize) / 2;
        const normalized = (value - minValue) / (maxValue - minValue);
        return minSize + normalized * (maxSize - minSize);
      };

      const fullTransformedData: EnrichmentPoint[] = allRawData.map(item => {
        const bgRatioNum = parseRatioToNumber(item.bgRatio);
        const oddsRatioNum = typeof item.oddsRatio === 'number' ? item.oddsRatio : 0;
        const pval = item.pvalue ?? 1;
        const intersection = item.intersectionSize ?? 0;
        const logP = pval > 0 ? -Math.log10(pval) : 100;
        const fullName = item.diseaseName || item.diseaseUMLSCUI || 'Unknown';
        const truncatedName = fullName.length > 35 ? `${fullName.substring(0, 25)}...` : fullName;
        const geneRatioValue = item.geneRatio || '0';

        return {
          id: fullName,
          group: truncatedName,
          value: bgRatioNum,
          pvalue: pval,
          diseaseName: fullName,
          diseaseUMLSCUI: item.diseaseUMLSCUI,
          intersectionSize: intersection,
          intersection: (item as any)?.intersection,
          variantRatio: item.variantRatio || '',
          bgRatioNum,
          oddsRatioNum,
          color: getColorByLogP(logP),
          size: calculateSize(intersection, bgRatioNum, oddsRatioNum, sizeMetric),
          geneRatioValue,
        };
      });

      const sortedByBgRatioAllItems = [...fullTransformedData].sort((a, b) => b.bgRatioNum - a.bgRatioNum);

      const sortedByIntersectionAllItems = [...fullTransformedData].sort(
        (a, b) => (b.intersectionSize ?? 0) - (a.intersectionSize ?? 0),
      );

      const sortedByOddsRatioAllItems = [...fullTransformedData].sort(
        (a, b) => (b.oddsRatioNum ?? 0) - (a.oddsRatioNum ?? 0),
      );

      const sortedByBgRatio = sortedByBgRatioAllItems.slice(0, 20);

      const sortedByIntersection = sortedByIntersectionAllItems.slice(0, 20);

      const sortedByOddsRatio = sortedByOddsRatioAllItems.slice(0, 20);

      const groupKeysBgRatio = sortedByBgRatio.map(d => d.group);
      const bgRatios = sortedByBgRatio.map(d => d.bgRatioNum).filter(r => r > 0);
      const minBgRatio = bgRatios.length > 0 ? Math.min(...bgRatios) : 0;
      const maxBgRatio = bgRatios.length > 0 ? Math.max(...bgRatios) : 1;

      const groupKeysIntersection = sortedByIntersection.map(d => d.group);
      const intersectionValues = sortedByIntersection.map(d => d.intersectionSize).filter(s => s > 0);
      const minIntersectionValue = intersectionValues.length > 0 ? Math.min(...intersectionValues) : 0;
      const maxIntersectionValue = intersectionValues.length > 0 ? Math.max(...intersectionValues) : 1;

      const groupKeysOddsRatio = sortedByOddsRatio.map(d => d.group);
      const oddsRatioValues = sortedByOddsRatio.map(d => d.oddsRatioNum ?? 0).filter(r => r > 0);
      const minOddsRatio = oddsRatioValues.length > 0 ? Math.min(...oddsRatioValues) : 0;
      const maxOddsRatio = oddsRatioValues.length > 0 ? Math.max(...oddsRatioValues) : 1;

      return {
        sortedByBgRatioAllItems,
        sortedByBgRatio,
        groupKeysBgRatio,
        minBgRatio,
        maxBgRatio,
        sortedByIntersectionAllItems,
        sortedByIntersection,
        groupKeysIntersection,
        minIntersectionValue,
        maxIntersectionValue,
        sortedByOddsRatioAllItems,
        sortedByOddsRatio,
        groupKeysOddsRatio,
        minOddsRatio,
        maxOddsRatio,
        minIntersection,
        maxIntersection,
        minBgRatioForSize,
        maxBgRatioForSize,
        minOddsRatioForSize,
        maxOddsRatioForSize,
        // limitedRawData,
      };
    }, [data, sizeMetric]);

    if (!processedData) return null;

    const {
      sortedByBgRatioAllItems,
      sortedByBgRatio,
      groupKeysBgRatio: defaultGroupKeysBgRatio,
      minBgRatio: defaultMinBgRatio,
      maxBgRatio: defaultMaxBgRatio,
      sortedByIntersectionAllItems,
      sortedByIntersection,
      groupKeysIntersection: defaultGroupKeysIntersection,
      minIntersectionValue: defaultMinIntersectionValue,
      maxIntersectionValue: defaultMaxIntersectionValue,
      sortedByOddsRatioAllItems,
      sortedByOddsRatio,
      groupKeysOddsRatio: defaultGroupKeysOddsRatio,
      minOddsRatio: defaultMinOddsRatio,
      maxOddsRatio: defaultMaxOddsRatio,
      minIntersection,
      maxIntersection,
      minBgRatioForSize,
      maxBgRatioForSize,
      minOddsRatioForSize,
      maxOddsRatioForSize,
    } = processedData;

    const { graphData, groupKeys, minPlotValue, maxPlotValue } = useMemo(() => {
      const sortedAllItems =
        plotMetric === 'bgRatio'
          ? sortedByBgRatioAllItems
          : plotMetric === 'oddsRatio'
            ? sortedByOddsRatioAllItems
            : sortedByIntersectionAllItems;

      const defaultGraphData =
        plotMetric === 'bgRatio'
          ? sortedByBgRatio
          : plotMetric === 'oddsRatio'
            ? sortedByOddsRatio
            : sortedByIntersection;
      const defaultGroupKeys =
        plotMetric === 'bgRatio'
          ? defaultGroupKeysBgRatio
          : plotMetric === 'oddsRatio'
            ? defaultGroupKeysOddsRatio
            : defaultGroupKeysIntersection;
      const defaultMinPlotValue =
        plotMetric === 'bgRatio'
          ? defaultMinBgRatio
          : plotMetric === 'oddsRatio'
            ? defaultMinOddsRatio
            : defaultMinIntersectionValue;
      const defaultMaxPlotValue =
        plotMetric === 'bgRatio'
          ? defaultMaxBgRatio
          : plotMetric === 'oddsRatio'
            ? defaultMaxOddsRatio
            : defaultMaxIntersectionValue;

      if (selectedDiseaseIds.size === 0) {
        return {
          graphData: defaultGraphData,
          groupKeys: defaultGroupKeys,
          minPlotValue: defaultMinPlotValue,
          maxPlotValue: defaultMaxPlotValue,
        };
      }
      const filtered = sortedAllItems.filter(item => selectedDiseaseIds.has(item.id));
      const capped = filtered.slice(0, MAX_DISEASES_IN_GRAPH);
      const keys = capped.map(d => d.group);

      const plotValues = capped
        .map(d =>
          plotMetric === 'bgRatio'
            ? d.bgRatioNum
            : plotMetric === 'oddsRatio'
              ? (d.oddsRatioNum ?? 0)
              : d.intersectionSize,
        )
        .filter(v => v > 0);

      return {
        graphData: capped,
        groupKeys: keys,
        minPlotValue: plotValues.length > 0 ? Math.min(...plotValues) : 0,
        maxPlotValue: plotValues.length > 0 ? Math.max(...plotValues) : 1,
      };
    }, [
      selectedDiseaseIds,
      plotMetric,
      sortedByBgRatioAllItems,
      sortedByBgRatio,
      sortedByIntersectionAllItems,
      sortedByIntersection,
      sortedByOddsRatioAllItems,
      sortedByOddsRatio,
      defaultGroupKeysBgRatio,
      defaultGroupKeysIntersection,
      defaultGroupKeysOddsRatio,
      defaultMinBgRatio,
      defaultMaxBgRatio,
      defaultMinIntersectionValue,
      defaultMaxIntersectionValue,
      defaultMinOddsRatio,
      defaultMaxOddsRatio,
    ]);

    const swarmPlotData = useMemo(() => {
      return graphData.map(d => ({
        ...d,
        value:
          plotMetric === 'bgRatio'
            ? d.bgRatioNum
            : plotMetric === 'oddsRatio'
              ? (d.oddsRatioNum ?? 0)
              : d.intersectionSize,
      }));
    }, [graphData, plotMetric]);

    const downloadSwarmPlotData = useMemo(() => {
      const downloadTopData =
        plotMetric === 'bgRatio'
          ? sortedByBgRatio
          : plotMetric === 'oddsRatio'
            ? sortedByOddsRatio
            : sortedByIntersection;
      return downloadTopData.map(d => ({
        ...d,
        value:
          plotMetric === 'bgRatio'
            ? d.bgRatioNum
            : plotMetric === 'oddsRatio'
              ? (d.oddsRatioNum ?? 0)
              : d.intersectionSize,
      }));
    }, [plotMetric, sortedByBgRatio, sortedByOddsRatio, sortedByIntersection]);

    const downloadTopData =
      plotMetric === 'bgRatio'
        ? sortedByBgRatio
        : plotMetric === 'oddsRatio'
          ? sortedByOddsRatio
          : sortedByIntersection;
    const downloadGroupKeys =
      plotMetric === 'bgRatio'
        ? defaultGroupKeysBgRatio
        : plotMetric === 'oddsRatio'
          ? defaultGroupKeysOddsRatio
          : defaultGroupKeysIntersection;
    const downloadMinPlotValue =
      plotMetric === 'bgRatio'
        ? defaultMinBgRatio
        : plotMetric === 'oddsRatio'
          ? defaultMinOddsRatio
          : defaultMinIntersectionValue;
    const downloadMaxPlotValue =
      plotMetric === 'bgRatio'
        ? defaultMaxBgRatio
        : plotMetric === 'oddsRatio'
          ? defaultMaxOddsRatio
          : defaultMaxIntersectionValue;

    const handleViewSelectedInSummary = useCallback(() => {
      if (selectedDiseaseIds.size === 0) return;
      const source = context?.restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      const idents = sortedByBgRatioAllItems
        .filter(item => selectedDiseaseIds.has(item.id))
        .map(item => item.diseaseUMLSCUI?.trim() || item.diseaseName?.trim())
        .filter(Boolean);
      if (idents.length === 0) return;
      navigate({
        pathname: '/search',
        search: `?view=DISEASES&idents=${idents.map(id => encodeURIComponent(id)).join('-')}&source=${source}&tab=GDA`,
      });
    }, [selectedDiseaseIds, sortedByBgRatioAllItems, context?.restrictSources, navigate]);

    const handleHover = (e: any, fullName: string) => {
      if (e && fullName) {
        const rect = (e.target as SVGElement).getBoundingClientRect();
        setHoveredLabel({ name: fullName, x: rect.left, y: rect.top });
      } else {
        setHoveredLabel(null);
      }
    };

    return (
      <div className="multiple-filters p-4 md:p-6 mt-8 relative">
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Enrichment Results</h2>
          {isTrial && (
            <small>
              Only the top 30 results are shown. Upgrade your account to see all results.{' '}
              <a href="/plans">View our plans</a>.
            </small>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="enrichment-plot-metric" className="text-sm font-medium text-gray-700">
                Plot:
              </label>
              <select
                id="enrichment-plot-metric"
                value={plotMetric}
                onChange={e => {
                  trackEvent({
                    name: 'plot_metric_change',
                    buttonId: `enrichment_plot_metric_${String(e.target.value)}`,
                    label: `Change plot metric to ${String(e.target.value)}`,
                    page: 'enrichment',
                    section: 'graph_controls',
                    feature: 'enrichment_graph',
                    eventCategory: 'enrichment',
                  });
                  setPlotMetric(e.target.value as PlotMetric);
                }}
                className="p-2 border border-gray-300 rounded-md bg-gray-100"
              >
                <option value="bgRatio">bgRatio</option>
                <option value="intersection">Intersection</option>
                <option value="oddsRatio">Odds Ratio</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="enrichment-size-metric" className="text-sm font-medium text-gray-700">
                Dot Size:
              </label>
              <select
                id="enrichment-size-metric"
                value={sizeMetric}
                onChange={e => {
                  trackEvent({
                    name: 'plot_size_metric_change',
                    buttonId: `enrichment_size_metric_${String(e.target.value)}`,
                    label: `Change size metric to ${String(e.target.value)}`,
                    page: 'enrichment',
                    section: 'graph_controls',
                    feature: 'enrichment_graph',
                    eventCategory: 'enrichment',
                  });
                  setSizeMetric(e.target.value as 'bgRatio' | 'oddsRatio' | 'intersection');
                }}
                className="p-2 border border-gray-300 rounded-md bg-gray-100"
              >
                <option value="intersection">Intersection</option>
                <option value="bgRatio">bgRatio</option>
                <option value="oddsRatio">Odds Ratio</option>
              </select>
            </div>
            <SharedButton
              variant="secondaryOutline"
              onClick={() => {
                trackEvent({
                  name: 'download_result',
                  buttonId: 'enrichment_download_graph_png',
                  label: 'Download graph png',
                  page: 'enrichment',
                  section: 'graph_controls',
                  feature: 'download',
                  eventCategory: 'enrichment',
                });
                onDownload();
              }}
              title="Download the enrichment graph as PNG image"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download Graph (PNG)
            </SharedButton>
          </div>
        </div>

        {/* Mobile Bar Chart - Only visible on mobile */}
        <div className="lg:hidden">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong className="font-semibold">Mobile-optimized view.</strong> For the full interactive
                  visualization, please visit from a desktop or tablet device.
                </p>
              </div>
            </div>
          </div>
          <MobileBarChart data={graphData} plotMetric={plotMetric} />
        </div>

        {/* Desktop SwarmPlot - Only rendered and visible on desktop */}
        <div
          ref={ref}
          className="hidden lg:block"
          style={{
            height: graphData.length > 0 ? '750px' : 'auto',
            position: 'relative',
            minHeight: graphData.length === 0 ? 120 : undefined,
          }}
        >
          {graphData.length > 0 ? (
            <div data-nivo="swarmplot" style={{ height: '100%', width: '100%' }}>
              <SwarmPlotChart
                data={swarmPlotData}
                groupKeys={groupKeys}
                minPlotValue={minPlotValue}
                maxPlotValue={maxPlotValue}
                plotMetric={plotMetric}
                onHover={handleHover}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-28 text-gray-500 text-sm">
              {selectedDiseaseIds.size > 0
                ? 'No selected diseases to display. Select at least one disease in the table below.'
                : 'Select diseases in the table below to display them in the graph.'}
            </div>
          )}
          {hoveredLabel && (
            <div
              style={{
                position: 'fixed',
                left: hoveredLabel.x - 10,
                top: hoveredLabel.y - 40,
                pointerEvents: 'none',
                zIndex: 1000,
              }}
              className="bg-gray-900 text-white px-3 py-2 rounded shadow-lg text-xs max-w-xs"
            >
              {hoveredLabel.name}
            </div>
          )}
        </div>

        {/* Legends - Hidden on mobile */}
        <div className="hidden lg:block mt-4 space-y-4">
          <ColorLegend />
          <SizeLegend
            minValue={
              sizeMetric === 'bgRatio'
                ? minBgRatioForSize
                : sizeMetric === 'oddsRatio'
                  ? minOddsRatioForSize
                  : minIntersection
            }
            maxValue={
              sizeMetric === 'bgRatio'
                ? maxBgRatioForSize
                : sizeMetric === 'oddsRatio'
                  ? maxOddsRatioForSize
                  : maxIntersection
            }
            metric={sizeMetric}
          />
        </div>

        {selectedDiseaseIds.size > MAX_DISEASES_IN_GRAPH && (
          <div
            className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
            role="status"
          >
            Only the first {MAX_DISEASES_IN_GRAPH} selected diseases are shown in the graph. Deselect some to
            see fewer, or scroll the table to change selection.
          </div>
        )}
        {selectedDiseaseIds.size > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                trackEvent({
                  name: 'navigation_click',
                  buttonId: 'enrichment_view_selected_in_summary',
                  label: 'View selected diseases in summary',
                  page: 'enrichment',
                  section: 'graph',
                  feature: 'navigation',
                  eventCategory: 'enrichment',
                });
                handleViewSelectedInSummary();
              }}
              className="filter-upload inline-flex items-center gap-2 text-sm"
              title="Open Disease Summary with all selected diseases"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              View selected in Summary ({selectedDiseaseIds.size}{' '}
              {selectedDiseaseIds.size === 1 ? 'disease' : 'diseases'})
            </button>
          </div>
        )}
        {/* Data Table */}
        <div className="mt-6">
          <DataTable
            data={
              plotMetric === 'bgRatio'
                ? sortedByBgRatioAllItems
                : plotMetric === 'oddsRatio'
                  ? sortedByOddsRatioAllItems
                  : sortedByIntersectionAllItems
            }
            selectedIds={selectedDiseaseIds}
            onSelectionChange={handleSelectionChange}
            geneFilterType={geneFilterType}
          />
        </div>

        {/* Hidden container for download */}
        <div
          id="download-container"
          className="bg-white p-6 rounded-lg shadow-lg"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
            width: '1200px',
            visibility: 'hidden',
          }}
        >
          <div className="text-center mb-6 py-3 border-b-2 border-gray-300">
            <p className="text-2xl font-bold text-gray-800 mb-3">Enrichment Results Visualization</p>
          </div>

          <div className="mb-6" style={{ height: '650px', position: 'relative' }}>
            <SwarmPlotChart
              data={downloadSwarmPlotData}
              groupKeys={downloadGroupKeys}
              minPlotValue={downloadMinPlotValue}
              maxPlotValue={downloadMaxPlotValue}
              plotMetric={plotMetric}
              isDownload={true}
            />
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <ColorLegend isDownload={true} />
              <SizeLegend
                minValue={
                  sizeMetric === 'bgRatio'
                    ? minBgRatioForSize
                    : sizeMetric === 'oddsRatio'
                      ? minOddsRatioForSize
                      : minIntersection
                }
                maxValue={
                  sizeMetric === 'bgRatio'
                    ? maxBgRatioForSize
                    : sizeMetric === 'oddsRatio'
                      ? maxOddsRatioForSize
                      : maxIntersection
                }
                metric={sizeMetric}
                isDownload={true}
              />
            </div>
            <DataTable data={downloadTopData} isDownload={true} />
          </div>
        </div>
      </div>
    );
  },
);

EnrichmentSwarmPlot.displayName = 'EnrichmentSwarmPlot';
export default EnrichmentSwarmPlot;
